'use client';

import { useCallback, useEffect, useRef } from 'react';

export const useProgressInViewport = ({
  containerRef,
  callback,
  startAtZero = true,
  startsFromBottom = false,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  callback: (progress: number) => void;
  startAtZero?: boolean;
  startsFromBottom?: boolean;
}) => {
  const latestAnimationFrame = useRef<number | null>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  const animate = useCallback(async () => {
    if (!containerRef.current) {
      latestAnimationFrame.current = requestAnimationFrame(animate);
      return;
    }

    const containerBoundingRect = containerRef.current.getBoundingClientRect();
    const posTopContainerToViewport = containerBoundingRect.top;
    const height = Math.max(containerBoundingRect.height, window.innerHeight);
    const progression = startAtZero
      ? Math.max(0, -posTopContainerToViewport / height)
      : -posTopContainerToViewport / height;

    // Changing the progression to positive. So, that it will be calculated from bottom
    const progressionX = startAtZero
      ? Math.max(0, posTopContainerToViewport / height)
      : -posTopContainerToViewport / height;

    if (startsFromBottom) {
      callback(progressionX);
    } else {
      callback(progression);
    }

    latestAnimationFrame.current = requestAnimationFrame(animate);
  }, []);

  const startAnimation: IntersectionObserverCallback = (entries) => {
    const entry = entries[0];

    if (entry && entry.isIntersecting && !latestAnimationFrame.current) {
      latestAnimationFrame.current = requestAnimationFrame(animate);
      return;
    }
    if (latestAnimationFrame.current) {
      cancelAnimationFrame(latestAnimationFrame.current);
    }
    latestAnimationFrame.current = null;
  };

  useEffect(() => {
    intersectionObserver.current = new IntersectionObserver(startAnimation, {
      root: null,
      rootMargin: '0px',
      threshold: 0.01,
    });
    if (containerRef.current) {
      intersectionObserver.current.observe(containerRef.current);
    }
    return () => {
      if (latestAnimationFrame.current) {
        cancelAnimationFrame(latestAnimationFrame.current);
      }
      intersectionObserver.current?.disconnect();
    };
  }, []);
};
